import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  autosaveKbFile,
  createKbFile,
  createKbFolder,
  deleteKbRecords,
  getAllKbRecords,
  getAutosavedKbFile,
  getKbDirectoryTree,
  getKbFileDetails,
  getKbFolderContents,
  getKbFolderDetails,
  getKbImportJobsStatus,
  getKbRecentlyViewed,
  getKbStarredRecords,
  kbFindPath,
  moveKbRecords,
  toggleKbRecordsStarredStatus,
  updateKbFile,
  updateKbFolder,
  updateViewedAt,
  saveSnapshotToKbFile,
  restoreOldVersion,
  createSnapshotFromRecord,
  getLiveKbFileDetails,
  uploadKbFiles,
  createShareableLink,
  getShareableLink,
  toggleShareableLink,
  getSharedArticle,
  sendKbEmail,
  unpublishKbFile,
  publishKbFileSnapshot,
} from "controller/api";
import {
  ApiErrorType,
  GetKbFolderContentsQueryProps,
  GetQueryProps,
  SendKbEmailInput,
} from "shared";
import { sentryLogger } from "utilz";
import { lastValueFrom } from "rxjs";
import { kbStore, setRecentlyCreatedFolderId } from "pages/modules/kb/store";

export const useCreateKbFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createKbFolder,
    onSuccess: (data, variables, context) => {
      // Set the newly created folder ID to show it first
      if (data && data[0]?.mapping_id) {
        kbStore.set(setRecentlyCreatedFolderId, data[0].mapping_id);
      }

      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "createKbFolder",
      });
    },
  });
};

export const useGetKbFolderDetails = (folderId: string) => {
  return useQuery({
    queryKey: ["kb", "folders", folderId],
    queryFn: () => getKbFolderDetails(folderId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbFolderDetails",
      });
    },
    enabled: !!folderId,
  });
};

export const useUpdateKbFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateKbFolder,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "updateKbFolder",
      });
    },
  });
};

export const useGetKbFolderContents = ({
  folderId,
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetKbFolderContentsQueryProps) => {
  return useQuery({
    queryKey: ["kb", "folders", folderId, "contents"],
    queryFn: () =>
      getKbFolderContents({
        folderId,
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbFolderContents",
      });
    },
  });
};

export const useCreateKbFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createKbFile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "createKbFile",
      });
    },
  });
};

export const useGetKbFileDetails = (fileId: string) => {
  return useQuery({
    queryKey: ["kb", "files", fileId],
    queryFn: () => getKbFileDetails(fileId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbFileDetails",
      });
    },
    enabled: fileId ? true : false,
  });
};

export const useGetLiveKbFileDetails = (
  articleId: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["kb", "files", articleId],
    queryFn: () => getLiveKbFileDetails(articleId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getLiveKbFileDetails",
      });
    },
    enabled: enabled !== undefined ? enabled : true,
  });
};

export const useUpdateKbFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateKbFile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
      queryClient.invalidateQueries(["kb"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "updateKbFile",
      });
    },
  });
};

export const useUpdateKbFileName = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateKbFile,
    onSuccess: (data, variables, context) => {
      // queryClient.invalidateQueries(["kb"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "updateKbFile",
      });
    },
  });
};

export const useSaveSnapshotToKbFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveSnapshotToKbFile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb"]);
      queryClient.invalidateQueries(["kb-directory-tree"]);
      queryClient.invalidateQueries(["autosaved-file"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "saveSnapshotToKbFile",
      });
    },
  });
};

export const useAutosaveUpdateKbFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: autosaveKbFile,
    onSuccess: (data, variables, context) => {
      // queryClient.invalidateQueries(["kb"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "updateKbFile",
      });
    },
  });
};

export const useGetAutosavedKbFile = (recordId: string) => {
  return useQuery({
    queryKey: ["autosaved-file", recordId],
    queryFn: () => getAutosavedKbFile(recordId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getAutosavedKbFile",
      });
    },
  });
};

export const useDeleteKbRecords = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteKbRecords,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
      queryClient.invalidateQueries(["kb", "files"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "deleteKbRecords",
      });
    },
  });
};

export const useGetAllKbRecords = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  return useQuery({
    queryKey: ["kb"],
    queryFn: () =>
      getAllKbRecords({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getAllKbRecords",
      });
    },
  });
};

export const useKbFindPath = (recordId: string) => {
  return useQuery({
    queryKey: ["kb", recordId, "path"],
    queryFn: () => kbFindPath(recordId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "kbFindPath",
      });
    },
  });
};

export const useMoveKbRecords = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: moveKbRecords,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "moveKbRecords",
      });
    },
  });
};

export const useToggleKbRecordsStarredStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleKbRecordsStarredStatus,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "toggleKbRecordsStarredStatus",
      });
    },
  });
};

export const useGetKbStarredRecords = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  return useQuery({
    queryKey: ["kb", "starred"],
    queryFn: () =>
      getKbStarredRecords({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbStarredRecords",
      });
    },
  });
};

export const useGetKbRecentlyViewed = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  return useQuery({
    queryKey: ["kb", "recently-viewed"],
    queryFn: () =>
      getKbRecentlyViewed({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbRecentlyViewed",
      });
    },
  });
};

export const useUpdateViewedAt = (recordId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["kb", "update-viewed-at"],
    queryFn: () => updateViewedAt(recordId),
    enabled: !!recordId,
    onSuccess: () => {
      queryClient.invalidateQueries(["kb", "recently-viewed"]);
    },
  });
};

export const useGetKbDirectoryTree = () => {
  return useQuery({
    queryKey: ["kb-directory-tree"],
    queryFn: () => getKbDirectoryTree(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbDirectoryTree",
      });
    },
  });
};

export const useRestoreOldVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: restoreOldVersion,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb"]);
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },

    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "restoreOldVersion",
      });
    },
  });
};

export const useCreateSnapshotFromRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSnapshotFromRecord,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb", "files"]);
      queryClient.invalidateQueries(["autosaved-file"]);
    },

    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "createSnapshotFromRecord",
      });
    },
  });
};

export const useUploadKbFiles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      files,
      folderId,
      onProgress,
    }: {
      files: File[];
      folderId?: string | null;
      onProgress?: (progress: number, uploaded: number, total: number) => void;
    }) => {
      const uploadSubject = uploadKbFiles({ files, folderId, onProgress });

      // Convert observable to promise
      return lastValueFrom(uploadSubject).then((event: any) => {
        if (event.status === "COMPLETE") {
          return event.response;
        }
        throw new Error("Upload failed");
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["import-status"]);
      queryClient.invalidateQueries(["kb-directory-tree"]);
      queryClient.invalidateQueries(["kb", "folders"]);
      queryClient.invalidateQueries(["kb", "files"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "uploadKbFiles",
      });
    },
  });
};

// ============================================================================
// Shareable Links Hooks
// ============================================================================

/**
 * Hook to create a shareable link for an article
 */
export const useCreateShareableLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createShareableLink,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["kb", "shareable-links", variables]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "createShareableLink",
      });
    },
  });
};

/**
 * Hook to get shareable link details by article ID
 */
export const useGetShareableLink = (articleId: string, enabled = true) => {
  return useQuery({
    queryKey: ["kb", "shareable-links", articleId],
    queryFn: () => getShareableLink(articleId),
    enabled: !!articleId && enabled,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getShareableLink",
      });
    },
  });
};

/**
 * Hook to toggle shareable link active status
 */
export const useToggleShareableLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      articleId,
      isActive,
    }: {
      articleId: string;
      isActive: boolean;
    }) => toggleShareableLink(articleId, isActive),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        "kb",
        "shareable-links",
        variables.articleId,
      ]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "toggleShareableLink",
      });
    },
  });
};

/**
 * Hook to get shared article (Public API - No authentication required)
 */
export const useGetSharedArticle = (mappingId: string, enabled = true) => {
  return useQuery({
    queryKey: ["kb", "public", "shared", mappingId],
    queryFn: () => getSharedArticle(mappingId),
    enabled: !!mappingId && enabled,
    retry: false,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getSharedArticle",
      });
    },
  });
};

/**
 * Hook to get KB import jobs status
 * Used to display import progress in KbImportStatus component
 * Query key "import-status" for MQTT cache invalidation
 */
export const useGetKbImportJobsStatus = (enabled = true) => {
  return useQuery({
    queryKey: ["import-status"],
    queryFn: () => getKbImportJobsStatus(),
    enabled,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "getKbImportJobsStatus",
      });
    },
  });
};

// ============================================================================
// Email Hooks
// ============================================================================

/**
 * Hook to send KB article via email
 */
export const useSendKbEmail = () => {
  return useMutation({
    mutationFn: (data: SendKbEmailInput) => sendKbEmail(data),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "sendKbEmail",
      });
    },
  });
};

export const useUnpublishKbFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unpublishKbFile,
    onSuccess: () => {
      queryClient.invalidateQueries(["kb"]);
      queryClient.invalidateQueries(["kb-directory-tree"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "unpublishKbFile",
      });
    },
  });
};

export const usePublishKbFileSnapshot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishKbFileSnapshot,
    onSuccess: () => {
      queryClient.invalidateQueries(["kb"]);
      queryClient.invalidateQueries(["autosaved-file"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "kb",
        functionName: "publishKbFileSnapshot",
      });
    },
  });
};
